import { useOrganization } from './OrganizationProvider.js'
import type { IOrganizationRef, IUser } from '../../../Model/index.js'
import { useEffect, useState } from 'react'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { OrganizationLogo } from '../../Component/Organization/OrganizationLogo.js'
import { UserIcon } from '@heroicons/react/16/solid/index.js'

interface UserAndOrgWidgetProps {
  id?: string | null
  userId?: string | null
  org?: IOrganizationRef | null
}

/*
 * `getUser`/`getOrganization` return 404 by design when the requester may not resolve the target
 * (cross-org lookup — e.g. a customer viewing a lab-created order resolving the lab creator's name).
 * That's an expected "not visible to you" outcome, not a fault, so we swallow 404s silently instead
 * of `console.error`-ing them (which Sentry auto-captures as noise). Any other failure is still logged.
 */
const isNotFound = (e: unknown): boolean =>
  !!e && typeof e === 'object' && 'response' in e && (e as { response?: { status?: number } }).response?.status === 404

export const UserAndOrgWidget = (props: UserAndOrgWidgetProps) => {
  const { getOrganization, getUser } = useOrganization()
  const [organization, setOrganization] = useState<IOrganizationRef | null>(null)
  const [cantFetch, setCantFetch] = useState(false)
  const [user, setUser] = useState<IUser | null>(null)
  const authUser = useGetAuthenticatedUser()

  useEffect(() => {
    if (props.org) {
      setOrganization(props.org)
    } else if (props.id) {
      getOrganization(props.id)
        .then(setOrganization)
        .catch((e) => {
          // 404 = not visible to this viewer (cross-org/private) → show the fallback without logging.
          if (!isNotFound(e)) {
            console.error(e)
          }
          setCantFetch(true)
        })
    }
    if (props.userId && authUser) {
      getUser(props.userId)
        .then(setUser)
        .catch((e) => {
          // 404 = cross-org name we may not resolve → leave the initials unset, no noise.
          if (!isNotFound(e)) {
            console.error(e)
          }
        })
    }
  }, [props.id, props.userId, authUser, getOrganization, props.org])

  if (!props.id) {
    return null
  } else if (cantFetch) {
    return <span>Private Organization</span>
  } else if (!organization) {
    return <span>Loading Org...</span>
  } else {
    return (
      <span className={"inline-flex flex-row items-center gap-2"}>
        <span className={"w-24 max-w-24 shrink-0 inline-flex flex-col justify-center border-2 border-rounded"} ><OrganizationLogo organization={organization} /></span>
        <span className={"inline-flex flex-col"}>
          <span>{organization.name}</span>

          {user && (<span className={"w-fit px-2 border rounded-sm inline-flex items-center justify-center"}>
              <UserIcon className={"w-8 h-8 inline"} />
              {user.firstName.charAt(0).toUpperCase()}{user.lastName.charAt(0).toUpperCase()}
            </span>
          )}
        </span>
      </span>
    )
  }
}
